import { TenantStore } from "./Store";
import { Person } from "./Person";
import { LoginMethod, Market } from "./Market";
import { Language, TranslationV2 } from "./Translation";

export interface TenantWriteModel {
  name: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  orgNo: string;
  address: string;
  city: string;
  postalCode: string;
  managerIds: ReadonlyArray<string>;
  externalPersonnelIds: ReadonlyArray<string>;
  tenantType: TenantType;
  market: Market;
  languageIds: ReadonlyArray<number>;

  loginMethods?: LoginMethod[];

  verificationMethods: LoginMethod[];
  paymentProviders?: PaymentProvider[];
  configuration: any;
  status: string;
  iban: string;
  bic: string;
}

export enum MobilePOSActivationType {
  NONE = 0,
  KRIS = 1,
  KRIS_HIGH = 2
}

export interface Tenant {
  id: string;
  customerId?: string;
  name: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  orgNo: string;
  address: string;
  city: string;
  postalCode: string;
  stores: ReadonlyArray<TenantStore>;
  managers: ReadonlyArray<Person>;
  externalPersonnel: ReadonlyArray<Person>;
  logoUrl?: string;
  bannerUrl?: string;
  tenantType: TenantType;
  market: Market;
  configuration?: TenantConfType;
  languageIds: number[];
  languages: ReadonlyArray<Language>;

  loginMethods?: LoginMethod[];
  verificationMethods: LoginMethod[];

  paymentProviders?: PaymentProvider[];
  numberOfReceiptEmailTemplates: number;
  iban: string;
  bic: string;
  status: string;
  content: Content;
  mobilePOSActive?: boolean;
  mobilePOSActivationType?: string;
}

export enum TenantTypeName {
  StoreKey = "StoreKey",
  WhiteLabel = "WhiteLabel",
  MobilePOS = "MobilePOS"
}

export const productCampaignsHiddenTenantTypes: ReadonlyArray<TenantTypeName> =
  [TenantTypeName.WhiteLabel, TenantTypeName.MobilePOS];

export const hidesProductCampaigns = (tenantTypeName: TenantTypeName) =>
  productCampaignsHiddenTenantTypes.includes(tenantTypeName);

export interface TenantType {
  id: number;
  name: TenantTypeName;
}

export interface TenantDistributorManager {
  id: string;
  personId: string;
  tenantDistributorId: string;
  distributorName: string;
  personName: string;
  active: boolean;
}

export interface TenantEndUser {
  companyName: string;
  contactName: string;
  contactEmail: string;
  orgNo: string;
  address: string;
  city: string;
  postalCode: string;
  iban: string;
  bic: string;
}

export type TenantConfType = {
  features?: Features;
  primaryColor?: string;
  receiptIntegration?: {
    customerId?: string;
  };
};

export type Features = {
  digitalPriceTags: boolean;
  centralOrderingSystem: boolean;
  clickNCollect: boolean;
  usesOpenPricing: boolean;
  requiresPaymentMethod: boolean;
  availableInStoreKey: boolean;
  deliveryThroughFileUpload: boolean;
  usesNotVisitedStoresLockOut: boolean;
  sendReceiptEmail: boolean;
  hasMultipleCombos: boolean;
  delivery: boolean;
};

export type FeatureType = keyof Features;

export enum TenantStatus {
  OnBoardReady = "OnBoardReady",
  OnBoardStep1Complete = "OnBoardStep1Complete",
  OnBoardStep2Complete = "OnBoardStep2Complete",
  OnBoardStep3Complete = "OnBoardStep3Complete",
  OnBoardReviewAndConfigComplete = "OnBoardReviewAndConfigComplete",
  Active = "Active"
}

export interface Content {
  support: Support;
  onBoarding: OnBoarding[];
}

export interface Support {
  helpId: string;
  faqId: string;
  legalId: string;
}

export interface OnBoarding {
  id: number;
  order: number;
  titleId: string;
  titleTranslation: TranslationV2;
  textId: string;
  textTranslation: TranslationV2;
  imageId: string;
  imageVersion: string;
  imageUrl: string;
}

export interface PaymentProvider {
  id: number;
  name: string;
  canBeCombined: boolean;
  displayOrder: number;
}
